/**
 * Snakes and Ladders Board Component
 *
 * Renders a 10×10 grid with boustrophedon numbering.
 * Draws snakes (red curves) and ladders (green lines).
 * Shows player tokens and highlights current player.
 */

import React, { useMemo } from 'react';
import {
  SnakeOrLadder,
  PlayerState,
  BoardPosition,
  BOARD_SIZE,
  TOTAL_SQUARES,
  SNAKES,
  LADDERS,
} from '../types';
import { SnakesLaddersTokens } from './SnakesLaddersTokens';
import './SnakesLaddersBoard.css';

interface SnakesLaddersBoardProps {
  players: PlayerState[];
  currentPlayerIndex: number;
  lastMove?: { fromPosition: number; toPosition: number; finalPosition: number } | null;
  animating?: boolean;
}

/**
 * Convert a board number (1–100) to row/col for boustrophedon layout.
 * Row 0 = bottom (numbers 1–10, left to right)
 * Row 1 = numbers 11–20, right to left
 * etc.
 */
export function getBoardPosition(num: number): BoardPosition {
  if (num < 1 || num > TOTAL_SQUARES) {
    return { number: num, row: 0, col: 0 };
  }
  const zeroIndex = num - 1;
  const row = Math.floor(zeroIndex / BOARD_SIZE);
  const colInRow = zeroIndex % BOARD_SIZE;
  // Even rows (0, 2, 4, ...) go left-to-right; odd rows go right-to-left
  const col = row % 2 === 0 ? colInRow : BOARD_SIZE - 1 - colInRow;
  return { number: num, row, col };
}

/**
 * Get the CSS grid position for a board number.
 * CSS grid row 1 = top of the board = row 9 in our numbering.
 */
function getCellStyle(num: number): React.CSSProperties {
  const pos = getBoardPosition(num);
  return {
    gridColumn: pos.col + 1,
    gridRow: BOARD_SIZE - pos.row, // Flip so row 0 (1–10) is at the bottom
  };
}

/** Get the center position of a cell as percentage for SVG overlay */
function getCellCenter(num: number): { x: number; y: number } {
  const pos = getBoardPosition(num);
  const cellSize = 100 / BOARD_SIZE;
  return {
    x: pos.col * cellSize + cellSize / 2,
    y: (BOARD_SIZE - 1 - pos.row) * cellSize + cellSize / 2,
  };
}

const SnakesLaddersBoard: React.FC<SnakesLaddersBoardProps> = ({
  players,
  currentPlayerIndex,
  lastMove,
  animating,
}) => {
  // Build the grid cells
  const cells = useMemo(() => {
    const result: BoardPosition[] = [];
    for (let i = 1; i <= TOTAL_SQUARES; i++) {
      result.push(getBoardPosition(i));
    }
    return result;
  }, []);

  // Build a lookup of which positions have snakes/ladders
  const entityMap = useMemo(() => {
    const map = new Map<number, SnakeOrLadder>();
    for (const s of SNAKES) map.set(s.start, s);
    for (const l of LADDERS) map.set(l.start, l);
    return map;
  }, []);

  const getCellClass = (num: number): string => {
    const classes = ['sl-board__cell'];
    const pos = getBoardPosition(num);
    // Checkerboard pattern
    if ((pos.row + pos.col) % 2 === 0) {
      classes.push('sl-board__cell--light');
    } else {
      classes.push('sl-board__cell--dark');
    }
    // Snake or ladder highlight
    const entity = entityMap.get(num);
    if (entity) {
      classes.push(entity.type === 'snake' ? 'sl-board__cell--snake' : 'sl-board__cell--ladder');
    }
    return classes.join(' ');
  };

  return (
    <div className="sl-board" role="grid" aria-label="Snakes and Ladders board">
      <div className="sl-board__grid">
        {cells.map((cell) => (
          <div
            key={cell.number}
            className={getCellClass(cell.number)}
            style={getCellStyle(cell.number)}
            role="gridcell"
            aria-label={`Square ${cell.number}${entityMap.has(cell.number) ? ` (${entityMap.get(cell.number)!.type} to ${entityMap.get(cell.number)!.end})` : ''}`}
          >
            <span className="sl-board__cell-number">{cell.number}</span>
          </div>
        ))}

        {/* SVG overlay for snakes and ladders */}
        <svg
          className="sl-board__overlay"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/* Ladders */}
          {LADDERS.map((ladder, i) => {
            const start = getCellCenter(ladder.start);
            const end = getCellCenter(ladder.end);
            const offset = 1.2;
            return (
              <g key={`ladder-${i}`} className="sl-board__ladder">
                {/* Left rail */}
                <line
                  x1={start.x - offset}
                  y1={start.y}
                  x2={end.x - offset}
                  y2={end.y}
                  stroke="var(--sl-ladder-color, #4caf50)"
                  strokeWidth="0.6"
                  strokeLinecap="round"
                />
                {/* Right rail */}
                <line
                  x1={start.x + offset}
                  y1={start.y}
                  x2={end.x + offset}
                  y2={end.y}
                  stroke="var(--sl-ladder-color, #4caf50)"
                  strokeWidth="0.6"
                  strokeLinecap="round"
                />
                {/* Rungs */}
                {Array.from({ length: 4 }, (_, r) => {
                  const t = (r + 1) / 5;
                  const rx = start.x + (end.x - start.x) * t;
                  const ry = start.y + (end.y - start.y) * t;
                  return (
                    <line
                      key={r}
                      x1={rx - offset}
                      y1={ry}
                      x2={rx + offset}
                      y2={ry}
                      stroke="var(--sl-ladder-rung-color, #388e3c)"
                      strokeWidth="0.4"
                      strokeLinecap="round"
                    />
                  );
                })}
              </g>
            );
          })}

          {/* Snakes */}
          {SNAKES.map((snake, i) => {
            const head = getCellCenter(snake.start);
            const tail = getCellCenter(snake.end);
            // Create a wavy S-curve snake body with multiple control points
            const dx = tail.x - head.x;
            const dy = tail.y - head.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            // Perpendicular direction for wave amplitude
            const px = -dy / len;
            const py = dx / len;
            const amp = Math.min(len * 0.2, 4); // Wave amplitude
            const waveDir = i % 2 === 0 ? 1 : -1;

            // 4 points along the snake for S-curve
            const p1x = head.x + dx * 0.25 + px * amp * waveDir;
            const p1y = head.y + dy * 0.25 + py * amp * waveDir;
            const p2x = head.x + dx * 0.5 - px * amp * waveDir;
            const p2y = head.y + dy * 0.5 - py * amp * waveDir;
            const p3x = head.x + dx * 0.75 + px * amp * waveDir * 0.5;
            const p3y = head.y + dy * 0.75 + py * amp * waveDir * 0.5;

            // Snake body path (cubic bezier S-curve)
            const bodyPath = `M ${head.x} ${head.y} C ${p1x} ${p1y}, ${p2x} ${p2y}, ${head.x + dx * 0.5} ${head.y + dy * 0.5} S ${p3x} ${p3y}, ${tail.x} ${tail.y}`;

            // Head direction for tongue/eyes
            const headAngle = Math.atan2(p1y - head.y, p1x - head.x);
            const eyeOffset = 0.6;
            const eyePerp = 0.5;

            return (
              <g key={`snake-${i}`} className="sl-board__snake">
                {/* Snake body — thick gradient stroke */}
                <path
                  d={bodyPath}
                  fill="none"
                  stroke="var(--sl-snake-color, #e53935)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.85"
                />
                {/* Snake body highlight (inner lighter line) */}
                <path
                  d={bodyPath}
                  fill="none"
                  stroke="var(--sl-snake-highlight, #ef5350)"
                  strokeWidth="0.8"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                {/* Snake body pattern (dashes for scales) */}
                <path
                  d={bodyPath}
                  fill="none"
                  stroke="var(--sl-snake-pattern, #c62828)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeDasharray="0.3 2"
                  opacity="0.4"
                />
                {/* Snake head — larger filled circle */}
                <circle
                  cx={head.x}
                  cy={head.y}
                  r="1.6"
                  fill="var(--sl-snake-head-color, #c62828)"
                  stroke="var(--sl-snake-color, #e53935)"
                  strokeWidth="0.3"
                />
                {/* Left eye */}
                <circle
                  cx={head.x + Math.cos(headAngle) * eyeOffset - Math.sin(headAngle) * eyePerp}
                  cy={head.y + Math.sin(headAngle) * eyeOffset + Math.cos(headAngle) * eyePerp}
                  r="0.35"
                  fill="var(--sl-snake-eye, #fff)"
                />
                {/* Right eye */}
                <circle
                  cx={head.x + Math.cos(headAngle) * eyeOffset + Math.sin(headAngle) * eyePerp}
                  cy={head.y + Math.sin(headAngle) * eyeOffset - Math.cos(headAngle) * eyePerp}
                  r="0.35"
                  fill="var(--sl-snake-eye, #fff)"
                />
                {/* Left pupil */}
                <circle
                  cx={head.x + Math.cos(headAngle) * eyeOffset - Math.sin(headAngle) * eyePerp}
                  cy={head.y + Math.sin(headAngle) * eyeOffset + Math.cos(headAngle) * eyePerp}
                  r="0.15"
                  fill="#000"
                />
                {/* Right pupil */}
                <circle
                  cx={head.x + Math.cos(headAngle) * eyeOffset + Math.sin(headAngle) * eyePerp}
                  cy={head.y + Math.sin(headAngle) * eyeOffset - Math.cos(headAngle) * eyePerp}
                  r="0.15"
                  fill="#000"
                />
                {/* Forked tongue */}
                <path
                  d={`M ${head.x + Math.cos(headAngle) * 1.5} ${head.y + Math.sin(headAngle) * 1.5} l ${Math.cos(headAngle - 0.4) * 1} ${Math.sin(headAngle - 0.4) * 1} M ${head.x + Math.cos(headAngle) * 1.5} ${head.y + Math.sin(headAngle) * 1.5} l ${Math.cos(headAngle + 0.4) * 1} ${Math.sin(headAngle + 0.4) * 1}`}
                  fill="none"
                  stroke="var(--sl-snake-tongue, #d32f2f)"
                  strokeWidth="0.25"
                  strokeLinecap="round"
                />
                {/* Tail — tapered end */}
                <circle
                  cx={tail.x}
                  cy={tail.y}
                  r="0.5"
                  fill="var(--sl-snake-color, #e53935)"
                  opacity="0.7"
                />
              </g>
            );
          })}
        </svg>

        {/* Player tokens */}
        <SnakesLaddersTokens
          players={players}
          currentPlayerIndex={currentPlayerIndex}
          animating={animating}
        />
      </div>
    </div>
  );
};

export { SnakesLaddersBoard };
export default SnakesLaddersBoard;
